import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Row,
  Col,
} from "reactstrap";
import { Icon } from "../../components/Component";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { useDispatch, useSelector } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import TopMenu from "../../route/topMenu";
import FormInput from "../form-components/InputComponent";
import FormTextArea from "../form-components/TextAreaComponent";
import FormDatePicker from "../form-components/DatePicker";
import FormSelect from "../form-components/SelectComponent";
import { FormProvider, useForm } from "react-hook-form";
import { expensesApis } from "../../api/Expenses";
import { projectsApis } from "../../api/projects";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { getDirtyFieldsOnly } from "../../utils/use-form/GetDirtyFieldsOnly";
import { Popover, Timeline, Badge } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from "@ant-design/icons";
import ExpenseApprovalDrawer from "./ExpenseApprovalDrawer";

interface BudgetLine {
  lineId: number;
  headerId: number;
  budgetId: string;
  budgetLineName: string;
  budgetValue: number;
  allocatedLineValue?: number;
}

interface ApprovalData {
  roleId: number | null;
  level: number;
  userId: number | null;
  approvalStage?: string;
  roleName?: string;
  userName?: string;
}

// Approval Timeline: Component for displaying approval hierarchy with status indicators
const ApprovalTimelineContent = ({ approvalsData }: { approvalsData: ApprovalData[] }) => {
  if (!approvalsData || approvalsData?.length === 0) {
    return <Badge color="outline-danger">No</Badge>;
  }

  return (
    <Popover
      content={
        <Timeline className="approval-timeline">
          {approvalsData?.map((approval: any, index: number) => {
            const isApproved = approval.status === "Approved";
            const isRejected = approval.status === "Rejected";
            return (
              <Timeline.Item
                key={index}
                dot={
                  isApproved ? (
                    <CheckCircleOutlined style={{ color: "green" }} />
                  ) : isRejected ? (
                    <CloseCircleOutlined style={{ color: "red" }} />
                  ) : undefined
                }
              >
                {approval?.userName || approval?.name}{" "}
                {approval?.reason && (
                  <Icon
                    name="info"
                    className="ms-1"
                    title={`Reason : ${approval?.reason}`}
                  />
                )}
                <div className="designation">{approval?.roleName}</div>
                {approval?.approvalStage && (
                  <div className="designation">{approval?.approvalStage}</div>
                )}
                {approval?.actionDate && (
                  <div className="designation">
                    {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                  </div>
                )}
              </Timeline.Item>
            );
          })}
        </Timeline>
      }
      title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
    >
      <span
        style={{
          color: "#2557d6",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        Yes
      </span>
    </Popover>
  );
};

const NewHeader: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ projectId: string }>();
  const projectIdFromUrl = params.projectId;
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState<string | number | null>(null);
  const [budgetLines, setBudgetLines] = useState<any[]>([]);
  const [selectedBudgetLine, setSelectedBudgetLine] = useState<BudgetLine | null>(null);
  const [budgetLinesLoading, setBudgetLinesLoading] = useState(false);
  const [budgetAllocationError, setBudgetAllocationError] = useState<string>("");
  const [editingReport, setEditingReport] = useState<any>(null);
  const [fetchingReportDetails, setFetchingReportDetails] = useState(false);
  const [openingApprovalDrawer, setOpeningApprovalDrawer] = useState(false);

  // Approval related states
  const [approvals, setApprovals] = useState({
    open: false,
    data: [] as ApprovalData[],
  });

  const methods = useForm({
    mode: "onChange",
  });

  // Helper function to get userId consistently
  const getUserId = () => {
    const userId = currentUser?.id || currentUser?.userId || currentUser?.data?.id || currentUser?.data?.userId;
    return userId;
  };

  // Navigation: Initialize menu data with current location state, ensuring projectId is preserved
  useEffect(() => {
    const { id: projectIdFromState, projectId: projectIdFromStateAlt } = location.state || {};
    const resolvedProjectId = projectIdFromUrl || projectIdFromState || projectIdFromStateAlt;
    const mergedLinkState = {
      ...(location.state || {}),
      ...(resolvedProjectId ? { id: resolvedProjectId, projectId: resolvedProjectId } : {}),
    };
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: mergedLinkState,
          };
        })
      )
    );
  }, [location, dispatch, projectIdFromUrl]);

  // State Management: Extract projectId from URL params first, then location state
  useEffect(() => {
    const { id: projectIdFromState, projectId: projectIdFromStateAlt } = location.state || {};
    const finalProjectId = projectIdFromUrl || projectIdFromState || projectIdFromStateAlt;

    if (finalProjectId) {
      setProjectId(finalProjectId);
    }
  }, [location.state, projectIdFromUrl]);

  // Data Loading: Load editing report data if in edit mode
  useEffect(() => {
    const { reportData, editMode } = location.state || {};

    if (editMode && reportData) {

      setEditingReport(reportData);

      // Populate form with existing data
      methods.setValue('reportName', reportData.reportName);
      methods.setValue('businessPurpose', reportData.businessPurpose || '');
      // Use budgetValue from API response if available, otherwise fall back to totalAmount
      // Format to 2 decimal places to avoid showing many trailing zeros
      const budgetValue = reportData.budgetValue || reportData.totalAmount;
      const formattedBudgetValue = budgetValue ? parseFloat(budgetValue.toString()).toFixed(2) : '';
      methods.setValue('budgetValue', formattedBudgetValue);

      // Set budget line if available
      if (reportData.budgetLineId) {
        methods.setValue('budgetLineId', reportData.budgetLineId);
        // Find and set the selected budget line for validation
        const budgetLine = budgetLines.find(line => line.value === reportData.budgetLineId);
        if (budgetLine?.extra) {
          setSelectedBudgetLine(budgetLine.extra);
        }
      }

      // Parse date range if available
      if (reportData.dateRange) {
        const [startDateStr, endDateStr] = reportData.dateRange.split(' - ');
        if (startDateStr) {
          const startDate = dayjs(startDateStr, 'DD-MMM-YYYY').startOf('day');
          if (startDate.isValid()) {
            methods.setValue('startDate', startDate);
          }
        }
        if (endDateStr) {
          const endDate = dayjs(endDateStr, 'DD-MMM-YYYY').startOf('day');
          if (endDate.isValid()) {
            methods.setValue('endDate', endDate);
          }
        }
      }

      // Set approval required field
      const hasApprovalData = reportData.extra?.approvalData || reportData.approvalData;
      methods.setValue('approvalRequired', hasApprovalData ? 'yes' : 'no');

      // Set approval data if available
      if (hasApprovalData) {
        const approvalData = reportData.extra?.approvalData || reportData.approvalData;
        methods.setValue('approvalsData', JSON.stringify(approvalData));
        setApprovals({
          open: false,
          data: Array.isArray(approvalData) ? approvalData : []
        });
      }

      // Fetch detailed report data to get complete approval hierarchy
      fetchDetailedReportData(reportData.id);
    }
  }, [location.state, methods, budgetLines]);

  // Data Loading: Fetch detailed report data with approval hierarchy
  const fetchDetailedReportData = async (reportId: string | number) => {
    try {
      setFetchingReportDetails(true);
      const userId = getUserId();
      const response = await expensesApis.fetchReport(reportId, {
        userid: userId,
        status: "Pending"
      });

      if (response && response.status && response.data) {
        const reportDetails = response.data;

        // Update editing report with detailed data
        const approvalDataForReport = reportDetails.expenseApprovalHierarchy ||
          reportDetails.approvalData ||
          (reportDetails.report && reportDetails.report[0] && reportDetails.report[0].expenseApprovalHierarchy) ||
          (reportDetails.report && reportDetails.report[0] && reportDetails.report[0].approvalData);

        setEditingReport((prev: any) => ({
          ...prev,
          ...reportDetails,
          // Ensure approval hierarchy is available
          approvalData: approvalDataForReport || prev?.approvalData,
          extra: {
            ...prev?.extra,
            approvalData: approvalDataForReport || prev?.extra?.approvalData
          }
        }));

        // Update form with detailed report data including budgetValue
        // Format to 2 decimal places to avoid showing many trailing zeros
        if (reportDetails.budgetValue) {
          const formattedBudgetValue = parseFloat(reportDetails.budgetValue.toString()).toFixed(2);
          methods.setValue('budgetValue', formattedBudgetValue);
        }

        // Update approval data state with fetched hierarchy
        // Check multiple possible locations for approval hierarchy data
        const approvalHierarchy = reportDetails.expenseApprovalHierarchy ||
          reportDetails.approvalData ||
          (reportDetails.report && reportDetails.report[0] && reportDetails.report[0].expenseApprovalHierarchy) ||
          (reportDetails.report && reportDetails.report[0] && reportDetails.report[0].approvalData);

        if (approvalHierarchy && Array.isArray(approvalHierarchy)) {
          setApprovals(prev => ({
            ...prev,
            data: approvalHierarchy
          }));
          methods.setValue('approvalsData', JSON.stringify(approvalHierarchy));
        }
      }
    } catch (error) {
      // Don't show error to user as basic data is already available
    } finally {
      setFetchingReportDetails(false);
    }
  };

  // Data Loading: Fetch budget lines when project is selected
  useEffect(() => {
    if (projectId) {
      fetchBudgetLines();
    }
  }, [projectId]);

  // Data Loading: Fetch budget lines for the selected project
  const fetchBudgetLines = async () => {
    try {
      const formattedProjectId = Number(projectId);
      setBudgetLinesLoading(true);
      const response: any = await projectsApis.fetchBudgetLines(
        formattedProjectId,
        { budgetFor: "Service" }
      );
      const { data: bgtLines, status } = response || {};

      if (status) {
        const formattedBudgetLines = bgtLines?.map((line: any) => ({
          label: line?.budgetLineName,
          value: line?.lineId,
          extra: line
        })) || [];
        setBudgetLines(formattedBudgetLines);
      }
    } catch (error) {
    } finally {
      setBudgetLinesLoading(false);
    }
  };

  // Budget Management: Handle budget line selection change
  const handleBudgetLineChange = (value: any, option: any) => {
    if (option?.extra) {
      setSelectedBudgetLine(option.extra);
    } else {
      setSelectedBudgetLine(null);
    }
    setBudgetAllocationError("");
  };

  // Budget Validation: Validate budget value in real-time
  const validateBudgetValue = (value: string) => {
    const raw = value === undefined || value === null ? "" : String(value);
    if (raw.trim() === "") {
      setBudgetAllocationError("");
      return;
    }

    const numValue = parseFloat(raw);
    if (isNaN(numValue)) {
      setBudgetAllocationError("Please enter a valid number");
      return;
    }

    if (numValue <= 0) {
      setBudgetAllocationError("Amount must be greater than 0");
      return;
    }

    // In edit mode, if we have a selected budget line or if we're editing an existing report
    const currentBudgetLine = selectedBudgetLine || (editingReport && budgetLines.find(line => line.value === editingReport.budgetLineId)?.extra);

    if (currentBudgetLine) {
      const remainingAmount = currentBudgetLine.budgetValue - (currentBudgetLine.allocatedLineValue || 0);
      if (numValue > remainingAmount) {
        setBudgetAllocationError(`Amount cannot exceed remaining budget of ₹${remainingAmount.toLocaleString("en-IN")}`);
        return;
      }
    } else if (!isEditMode) {
      // Only show this error in create mode
      setBudgetAllocationError("Please select a budget line first");
      return;
    }

    setBudgetAllocationError("");
  };

  // Trigger validation when selectedBudgetLine changes
  useEffect(() => {
    if (selectedBudgetLine && methods.watch("budgetValue")) {
      methods.trigger("budgetValue");
      validateBudgetValue(methods.watch("budgetValue"));
    }
  }, [selectedBudgetLine, methods]);

  // Site Management: Get siteId for approval drawer
  const getSiteId = async () => {
    const { siteId } = location.state || {};
    let siteIdToUse = siteId;

    // If siteId is not available in location state, fetch it from project data
    if (!siteIdToUse && projectId) {
      try {
        const projectResponse = await projectsApis.getProjectById(projectId.toString(), null);
        if (projectResponse && projectResponse.status && projectResponse.data) {
          siteIdToUse = projectResponse.data.siteId;
        }
      } catch (error) {
        // Fallback to projectId if API call fails
        siteIdToUse = Number(projectId);
      }
    }

    return siteIdToUse;
  };

  // Approval Management: Handle opening approval drawer with loader
  const handleOpenApprovalDrawer = async () => {
    try {
      setOpeningApprovalDrawer(true);
      // Small delay to show loader
      await new Promise(resolve => setTimeout(resolve, 500));
      setApprovals((pre) => {
        return {
          ...pre,
          open: true,
        };
      });
    } catch (error) {
    } finally {
      setOpeningApprovalDrawer(false);
    }
  };

  // Form Submission: Handle form submission with validation and API calls
  const handleSubmit = async (data: any) => {
    try {
      setLoading(true);

      if (!projectId) {
        Swal.fire({
          title: "Error!",
          text: "Project ID is required to create a report",
          icon: "error",
          confirmButtonText: "OK"
        });
        setLoading(false);
        return;
      }

      // Get dirty fields for edit mode
      let dirtyData: any = {};
      if (editingReport) {
        const { dirtyFields } = methods.formState;
        dirtyData = getDirtyFieldsOnly(data, dirtyFields) || {};

        // If no dirty fields, show message and return
        if (Object.keys(dirtyData).length === 0) {
          Swal.fire({
            title: "No Changes",
            text: "No changes were made to the report",
            icon: "info",
            confirmButtonText: "OK"
          });
          setLoading(false);
          return;
        }
      }

      // Validate budget value if provided
      if (data.budgetValue && selectedBudgetLine) {
        const numValue = parseFloat(data.budgetValue);
        const remainingAmount = selectedBudgetLine.budgetValue - (selectedBudgetLine.allocatedLineValue || 0);
        if (numValue > remainingAmount) {
          Swal.fire({
            title: "Validation Error!",
            text: `Amount cannot exceed remaining budget of ₹${remainingAmount.toLocaleString("en-IN")}`,
            icon: "error",
            confirmButtonText: "OK"
          });
          setLoading(false);
          return;
        }
      }

      let payloadData: any = {};

      if (editingReport) {
        // For edit mode, only include dirty fields
        if (dirtyData.budgetLineId !== undefined) {
          payloadData.budgetLineId = parseInt(data.budgetLineId);
        }
        if (dirtyData.reportName !== undefined) {
          payloadData.reportName = data.reportName;
        }
        if (dirtyData.businessPurpose !== undefined) {
          payloadData.businessPurpose = data.businessPurpose;
        }
        if (dirtyData.startDate !== undefined) {
          payloadData.startDate = dayjs(data.startDate).startOf('day').format("YYYY-MM-DD");
        }
        if (dirtyData.endDate !== undefined) {
          payloadData.endDate = dayjs(data.endDate).startOf('day').format("YYYY-MM-DD");
        }
        if (dirtyData.approvalRequired !== undefined) {
          payloadData.approvalRequired = data.approvalRequired === "yes";
        }
        if (dirtyData.budgetValue !== undefined && data.budgetValue) {
          payloadData.budgetValue = parseFloat(data.budgetValue);
        }
        if (dirtyData.approvalRequired !== undefined) {
          // Always include expenseApprovalHierarchy when approvalRequired is changed
          if (data.approvalRequired === "yes" && approvals.data && approvals.data.length > 0) {
            payloadData.expenseApprovalHierarchy = approvals.data;
          } else {
            payloadData.expenseApprovalHierarchy = [];
          }
        }
        if (
          data.approvalRequired === "no" &&
          dirtyData.autoApprovalRemarks !== undefined
        ) {
          payloadData.autoApprovalRemarks = data.autoApprovalRemarks;
        }
      } else {
        // For create mode, include all fields
        // When sequence is not set, backend uses id for creation (same as other modules)
        payloadData = {
          id: Date.now(),
          budgetLineId: parseInt(data.budgetLineId),
          projectId: Number(projectId),
          reportName: data.reportName,
          businessPurpose: data.businessPurpose,
          startDate: dayjs(data.startDate).startOf('day').format("YYYY-MM-DD"),
          endDate: dayjs(data.endDate).startOf('day').format("YYYY-MM-DD"),
          approvalRequired: data.approvalRequired === "yes"
        };

        // Add budget value if provided
        if (data.budgetValue) {
          payloadData.budgetValue = parseFloat(data.budgetValue);
        }

        // Always include expenseApprovalHierarchy, even if empty
        if (data.approvalRequired === "yes" && approvals.data && approvals.data.length > 0) {
          payloadData.expenseApprovalHierarchy = approvals.data;
        } else {
          payloadData.expenseApprovalHierarchy = [];
        }

        if (data.approvalRequired === "no" && data.autoApprovalRemarks) {
          payloadData.autoApprovalRemarks = data.autoApprovalRemarks;
        }
      }

      // Wrap the payload in a data object as expected by the API
      const apiPayload = {
        data: payloadData
      };

      let response;
      if (editingReport) {
        // Update existing report - always include required fields
        const updatePayload = {
          expenseReportId: parseInt(editingReport.id),
          projectId: Number(projectId),
          ...payloadData
        };

        response = await expensesApis.updateExpenseReport(updatePayload);
      } else {
        // Create new report
        response = await expensesApis.createExpenseReport(apiPayload);
      }

      if (response && response.status) {
        Swal.fire({
          title: "Success!",
          text: editingReport ? "Report updated successfully" : "Report created successfully",
          icon: "success",
          confirmButtonText: "OK"
        });

        // Navigate back to reports
        navigate(-1);
      } else {
        throw new Error(response?.message || `Failed to ${editingReport ? 'update' : 'create'} report`);
      }
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: error instanceof Error ? error.message : `Failed to ${editingReport ? 'update' : 'create'} report`,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
    }
  };

  // Navigation: Handle back button navigation
  const handleBack = () => {
    navigate(-1);
  };

  const isEditMode = editingReport !== null;

  return (
    <>
      <Head title={isEditMode ? "Edit Header" : "New Header"} />
      <Content>
        <Card className="card-bordered">
          {/* Full card view without aside menu */}
          <div className="card-inner" style={{ minHeight: "87vh" }}>
            <FormProvider {...methods}>
              <div>
                <div
                  className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                  style={{ minHeight: "10px", border: "0px" }}
                >
                  <h5>
                    {isEditMode ? `Edit ${editingReport?.reportName}` : "New Header Creation"}
                  </h5>
                  <div className="toggle-wrap nk-block-tools-toggle">
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0">
                      <li className="pt-0 pb-0">
                        <Button
                          className="toggle"
                          color="primary"
                          size="sm"
                          onClick={handleBack}
                          style={{ borderRadius: "6px", height: "32px", fontSize: '12px', padding: '4px 12px', fontWeight: "500", fontFamily: 'Inter, Roboto, sans-serif' }}
                        >
                          <Icon name="arrow-left" />
                          <span>Back</span>
                        </Button>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Main Content */}
                <div>
                  <form onSubmit={methods.handleSubmit(handleSubmit)}>
                    <div>
                      {!isEditMode && (
                        <div className="mb-3">
                          <FormInput
                            name="reportName"
                            label="Expense Header Name"
                            placeholder="Enter Expense Header Name"
                            value={methods.watch("reportName") || ""}
                            type="text"
                            required={true}
                          />
                        </div>
                      )}

                      <div className="row mt-4">
                        <div className="col-md-6 mb-4">
                          <FormDatePicker
                            name="startDate"
                            label="Start Date"
                            required={true}
                            dateFormat="DD/MM/YYYY"
                          />
                        </div>
                        <div className="col-md-6 mb-4">
                          <FormDatePicker
                            name="endDate"
                            label="End Date"
                            required={true}
                            dateFormat="DD/MM/YYYY"
                            customValidation={{
                              validate: (selectedDate) => {
                                const startDate = methods.watch("startDate");
                                if (startDate && selectedDate) {
                                  const start = dayjs(startDate);
                                  if (dayjs(selectedDate).isBefore(start)) {
                                    return "End date must be after start date";
                                  }
                                }
                                return true;
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-6">
                          <FormSelect
                            name="budgetLineId"
                            placeholder="Select Budget Line"
                            options={budgetLines}
                            required={true}
                            onChange={handleBudgetLineChange}
                            isLoading={budgetLinesLoading}
                            label={
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                <span>Budget Line</span>
                                {selectedBudgetLine && (
                                  <span className="position-relative d-inline-block">
                                    <Icon
                                      name="info"
                                      className="ms-1"
                                      style={{ color: "#1890ff", cursor: "pointer", fontSize: "16px" }}
                                      title={`Budget Value: ₹${selectedBudgetLine.budgetValue?.toLocaleString("en-IN")}
Allocated Value: ₹${selectedBudgetLine.allocatedLineValue?.toLocaleString("en-IN") || "0"}
Remaining: ₹${(selectedBudgetLine.budgetValue - (selectedBudgetLine.allocatedLineValue || 0))?.toLocaleString("en-IN")}`}
                                    />
                                  </span>
                                )}
                              </span>
                            }
                          />
                        </div>
                        <div className="col-6">
                          <FormInput
                            name="budgetValue"
                            label={
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: "4px",
                                }}
                              >
                                <span>Budget Value</span>
                                {selectedBudgetLine && (
                                  <span
                                    style={{
                                      color: "#52c41a",
                                      fontWeight: "normal",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    (Avl: ₹
                                    {(
                                      selectedBudgetLine.budgetValue -
                                      (selectedBudgetLine.allocatedLineValue ||
                                        0)
                                    ).toLocaleString("en-IN")}
                                    )
                                  </span>
                                )}
                              </span>
                            }
                            placeholder="Enter budget value for this report"
                            type="number"
                            required={true}
                            customValidation={{
                              validate: (value) => {
                                const raw =
                                  value === undefined || value === null
                                    ? ""
                                    : String(value);
                                if (raw.trim() === "") {
                                  return true;
                                }

                                const numValue = parseFloat(raw);
                                if (isNaN(numValue)) {
                                  return "Please enter a valid number";
                                }

                                if (numValue <= 0) {
                                  return "Amount must be greater than 0";
                                }

                                const currentBudgetLine =
                                  selectedBudgetLine ||
                                  (editingReport &&
                                    budgetLines.find(
                                      (line) =>
                                        line.value ===
                                        editingReport.budgetLineId,
                                    )?.extra);

                                if (currentBudgetLine) {
                                  const remainingAmount =
                                    currentBudgetLine.budgetValue -
                                    (currentBudgetLine.allocatedLineValue || 0);
                                  if (numValue > remainingAmount) {
                                    return `Amount cannot exceed available budget of ₹${remainingAmount.toLocaleString("en-IN")}`;
                                  }
                                } else if (!isEditMode) {
                                  return "Please select a budget line first";
                                }
                                return true;
                              },
                            }}
                            onInput={(
                              event: React.FormEvent<HTMLInputElement>,
                            ) => {
                              const value = event.currentTarget.value;
                              validateBudgetValue(value);
                              methods.trigger("budgetValue");
                            }}
                          />
                        </div>
                      </div>

                      <div className="row mt-3">
                        <div className="col-6">
                          {isEditMode ? (
                            // Show disabled field with approval hierarchy for edit mode
                            <div>
                              <label className="form-label">
                                Approval Required ?
                                {(() => {
                                  const approvalData = approvals.data.length > 0 ? approvals.data :
                                    (editingReport.extra?.approvalData || editingReport.approvalData);
                                  if (approvalData && Array.isArray(approvalData) && approvalData.length > 0) {
                                    return (
                                      <span style={{ marginLeft: "8px", fontSize: "12px", color: "#1890ff" }}>
                                        <Icon name="info" title="Hover over the field below to view approval hierarchy" />
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </label>
                              <div
                                style={{
                                  padding: "8px 12px",
                                  border: "1px solid #d9d9d9",
                                  borderRadius: "4px",
                                  backgroundColor: "#f5f5f5",
                                  minHeight: "32px",
                                  display: "flex",
                                  alignItems: "center",
                                  transition: "all 0.2s ease",
                                  cursor: "default"
                                }}
                                onMouseEnter={(e) => {
                                  const approvalData = approvals.data.length > 0 ? approvals.data :
                                    (editingReport.extra?.approvalData || editingReport.approvalData);
                                  if (approvalData && Array.isArray(approvalData) && approvalData.length > 0) {
                                    e.currentTarget.style.backgroundColor = "#e6f7ff";
                                    e.currentTarget.style.borderColor = "#1890ff";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  const approvalData = approvals.data.length > 0 ? approvals.data :
                                    (editingReport.extra?.approvalData || editingReport.approvalData);
                                  if (approvalData && Array.isArray(approvalData) && approvalData.length > 0) {
                                    e.currentTarget.style.backgroundColor = "#f5f5f5";
                                    e.currentTarget.style.borderColor = "#d9d9d9";
                                  }
                                }}
                              >
                                {(() => {
                                  if (fetchingReportDetails) {
                                    return (
                                      <span style={{ color: "#666", display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Icon name="spinner" className="spinner" />
                                        Loading approval hierarchy...
                                      </span>
                                    );
                                  }

                                  // Use the most up-to-date approval data from state
                                  const approvalData = approvals.data.length > 0 ? approvals.data :
                                    (editingReport.extra?.approvalData || editingReport.approvalData);


                                  if (approvalData && Array.isArray(approvalData) && approvalData.length > 0) {
                                    return <ApprovalTimelineContent approvalsData={approvalData} />;
                                  } else {
                                    return <span style={{ color: "#666" }}>No</span>;
                                  }
                                })()}
                              </div>

                              {/* Hover tooltip for approval hierarchy */}
                              {(() => {
                                const approvalData = approvals.data.length > 0 ? approvals.data :
                                  (editingReport.extra?.approvalData || editingReport.approvalData);

                                if (approvalData && Array.isArray(approvalData) && approvalData.length > 0) {
                                  return (
                                    <div className="mt-2">
                                      <Popover
                                        placement="rightTop"
                                        content={
                                          <Timeline className="approval-timeline">
                                            {approvalData?.map((approval: any, index: number) => {
                                              const isApproved = approval.status === "Approved";
                                              const isRejected = approval.status === "Rejected";
                                              return (
                                                <Timeline.Item
                                                  key={index}
                                                  dot={
                                                    isApproved ? (
                                                      <CheckCircleOutlined style={{ color: "green" }} />
                                                    ) : isRejected ? (
                                                      <CloseCircleOutlined style={{ color: "red" }} />
                                                    ) : undefined
                                                  }
                                                >
                                                  <div style={{ marginBottom: '4px' }}>
                                                    <strong style={{ color: '#1890ff' }}>
                                                      {approval?.userName || approval?.name || `User ${approval?.userId || 'Unknown'}`}
                                                    </strong>
                                                  </div>
                                                  {approval?.roleName && (
                                                    <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                                                      Role: {approval.roleName}
                                                    </div>
                                                  )}
                                                  {approval?.approvalStage && (
                                                    <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                                                      Stage: {approval.approvalStage}
                                                    </div>
                                                  )}
                                                  <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                                                    Level: {approval?.level || `Level ${index + 1}`}
                                                  </div>
                                                  {approval?.actionDate && (
                                                    <div className="designation" style={{ color: '#666', fontSize: '12px' }}>
                                                      Date: {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                                                    </div>
                                                  )}
                                                  {approval?.reason && (
                                                    <div style={{ marginTop: '4px' }}>
                                                      <Icon
                                                        name="info"
                                                        className="ms-1"
                                                        title={`Reason : ${approval?.reason}`}
                                                      />
                                                    </div>
                                                  )}
                                                </Timeline.Item>
                                              );
                                            })}
                                          </Timeline>
                                        }
                                        title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
                                      >
                                        <span
                                          style={{
                                            color: "#2557d6",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "4px"
                                          }}
                                        >
                                          <Icon name="eye" style={{ fontSize: '12px' }} />
                                          View Approval Hierarchy
                                        </span>
                                      </Popover>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              {editingReport.status === 'PENDING' || editingReport.status === 'PENDING_APPROVAL' ? (
                                <div className="mt-2">
                                  <small className="text-muted">
                                    <Icon name="info" className="me-1" />
                                    Approval settings cannot be modified for pending reports
                                  </small>
                                </div>
                              ) : (
                                <div className="mt-2">
                                  <a
                                    href="#"
                                    className="success"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleOpenApprovalDrawer();
                                    }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      pointerEvents: openingApprovalDrawer ? "none" : "auto",
                                      opacity: openingApprovalDrawer ? 0.6 : 1
                                    }}
                                  >
                                    {openingApprovalDrawer ? (
                                      <>
                                        <Icon name="spinner" className="spinner me-1" />
                                        Opening...
                                      </>
                                    ) : (
                                      <>
                                        <Icon name="edit" className="me-1" />
                                        Edit Approvals &nbsp;
                                        {approvals?.data?.length > 0 && (
                                          <span style={{
                                            backgroundColor: "#1890ff",
                                            color: "white",
                                            borderRadius: "50%",
                                            padding: "2px 6px",
                                            fontSize: "10px",
                                            fontWeight: "bold",
                                            marginLeft: "4px"
                                          }}>
                                            {approvals.data.length}
                                          </span>
                                        )}
                                        {approvals?.data?.length === 0 ? (
                                          <span className="text-danger" style={{ fontSize: "12px" }}>
                                            Please set approvals
                                          </span>
                                        ) : (
                                          <span className="text-success" style={{ fontSize: "12px" }}>
                                            ({approvals?.data?.length} approval{approvals?.data?.length !== 1 ? 's' : ''} set)
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </a>
                                </div>
                              )}
                            </div>
                          ) : (
                            // Show normal form field for create mode
                            <>
                              <FormSelect
                                name="approvalRequired"
                                label="Approval Required ?"
                                options={[
                                  { label: "Yes", value: "yes" },
                                  { label: "No", value: "no" },
                                ]}
                                placeholder="Select option"
                                required={true}
                              />
                              <div style={{ display: "none" }}>
                                <FormInput name="approvalsData" label="" type="text" />
                              </div>
                              {methods.watch("approvalRequired") === "yes" && (
                                <div className="mt-2">
                                  <a
                                    href="#"
                                    className="success"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleOpenApprovalDrawer();
                                    }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      pointerEvents: openingApprovalDrawer ? "none" : "auto",
                                      opacity: openingApprovalDrawer ? 0.6 : 1
                                    }}
                                  >
                                    {openingApprovalDrawer ? (
                                      <>
                                        <Icon name="spinner" className="spinner me-1" />
                                        Opening...
                                      </>
                                    ) : (
                                      <>
                                        <Icon name="edit" className="me-1" />
                                        Set Approvals &nbsp;
                                        {approvals?.data?.length === 0 ? (
                                          <span className="text-danger" style={{ fontSize: "12px" }}>
                                            Please set approvals
                                          </span>
                                        ) : (
                                          <span className="text-success" style={{ fontSize: "12px" }}>
                                            ({approvals?.data?.length} approval{approvals?.data?.length !== 1 ? 's' : ''} set)
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </a>
                                </div>
                              )}
                              {methods.watch("approvalRequired") === "no" && (
                                <div className="mt-2">
                                  <FormInput
                                    name="autoApprovalRemarks"
                                    label="Remarks for No Approval"
                                    type="textarea"
                                    rows={2}
                                    required={true}
                                    minLength={2}
                                    maxLength={500}
                                    placeholder="Enter reason for not requiring approvals"
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <FormTextArea
                          name="businessPurpose"
                          label="Business Purpose"
                          placeholder="Max 500 characters"
                          required={true}
                        />
                      </div>

                      <Row className="mt-4">
                        <Col lg="12">
                          <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                            <li>
                              <Button
                                className="btn btn-primary btn-md"
                                type="submit"
                                color="primary"
                                disabled={loading}
                                style={{ height: "32px", fontSize: "12px", padding: "4px 12px" }}
                              >
                                <span>{isEditMode ? "Update" : "Save"}</span>
                              </Button>
                            </li>
                            <li>
                              <a
                                href="#cancel"
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  handleBack();
                                }}
                                className="link link-light"
                              >
                                Cancel
                              </a>
                            </li>
                          </ul>
                        </Col>
                      </Row>
                    </div>
                  </form>
                </div>
              </div>
            </FormProvider>
          </div>
        </Card>
      </Content>

      {/* Approval Drawer */}
      {approvals.open && (
        <ExpenseApprovalDrawer
          open={approvals.open}
          siteId={getSiteId}
          onClose={() => {
            setApprovals((pre) => {
              return {
                ...pre,
                open: false,
              };
            });
          }}
          onSubmit={(data: any) => {
            setApprovals({
              open: false,
              data: data,
            });
            // Update form with approval data
            methods.setValue("approvalsData", JSON.stringify(data));

            // Show success message
            Swal.fire({
              title: "Success!",
              text: `Approvals saved successfully! ${data.length} approval level${data.length !== 1 ? 's' : ''} configured.`,
              icon: "success",
              confirmButtonText: "OK",
              timer: 3000,
              timerProgressBar: true
            });
          }}
          setApprovalSubmissionStatus={(status: boolean) => { }}
          approvalData={approvals.data as any}
        />
      )}
    </>
  );
};

export default NewHeader;
